import type { ChecklistResponse, ChecklistSection, ChecklistStep } from 'entities/onboarding/types';

export type ChecklistProgress = {
  current: ChecklistStep;
  /** 1-based position of `current` among all steps. */
  position: number;
  total: number;
};

export function blockingStepId(step: ChecklistStep): string | null {
  return step.blocked_by?.[0] ?? null;
}

export function flattenSteps(sections: ChecklistSection[]): ChecklistStep[] {
  return sections.flatMap((section) => section.steps);
}

/**
 * The step the user is on: the first pending one they can actually act on.
 *
 * A well-ordered catalog puts a prerequisite before what it blocks, so the first pending step is
 * normally unlocked anyway; the fallback covers a catalog edit that reorders them.
 */
export function currentStep(steps: ChecklistStep[]): ChecklistStep | null {
  const pending = steps.filter((step) => step.status === 'pending');
  return pending.find((step) => !step.locked) ?? pending[0] ?? null;
}

/** Where the user is up to, or null once every step is settled. */
export function checklistProgress(checklist: ChecklistResponse): ChecklistProgress | null {
  const steps = flattenSteps(checklist.sections);
  const current = currentStep(steps);
  return current ? { current, position: steps.indexOf(current) + 1, total: checklist.total_count } : null;
}
